(() => {
  if (customElements.get('dietxp-search')) return;
  class DietXPSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.listeners = new AbortController();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.dxsr__suggestions');
      this.status = this.querySelector('[role="status"]');
      this.clear = this.querySelector('[type="reset"]');
      this.discover = this.closest('dialog, .dxsr-results')?.querySelector('[data-dx-search-discover]');
      this.version = 0;
      const options = { signal: this.listeners.signal };
      this.input.addEventListener('input', () => this.update(), options);
      this.querySelector('form').addEventListener('reset', event => {
        event.preventDefault();
        this.input.value = '';
        this.update();
        this.input.focus();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.target !== this.input && !this.results.contains(event.target)) return;
        const links = Array.from(this.results.querySelectorAll('a'));
        if (this.results.hidden || !links.length) return;
        const index = links.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          links[Math.min(index + 1, links.length - 1)].focus();
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          if (index <= 0) this.input.focus();
          else links[index - 1].focus();
        }
      }, options);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.listeners?.abort();
      this.ready = false;
    }
    update() {
      clearTimeout(this.timer);
      this.controller?.abort();
      const version = ++this.version;
      const query = this.input.value.trim();
      this.clear.hidden = !query;
      this.results.hidden = true;
      this.results.replaceChildren();
      this.status.textContent = '';
      this.removeAttribute('aria-busy');
      if (this.discover) this.discover.hidden = Boolean(query);
      if (!query) return;
      this.timer = setTimeout(() => this.search(query, version), 250);
    }
    element(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    link(raw, className) {
      const url = new URL(raw, location.origin);
      if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid result URL');
      const link = this.element('a', className);
      link.href = url.href;
      return link;
    }
    arrow() {
      const arrow = this.element('span', 'dxsr-icon', '→');
      arrow.setAttribute('aria-hidden', 'true');
      return arrow;
    }
    renderResults(results, query) {
      const content = this.element('div');
      content.dataset.dxSearchResponse = '';
      const products = results.products || [];
      const pages = results.pages || [];
      const articles = results.articles || [];
      content.dataset.count = String(products.length + pages.length + articles.length);
      const currency = this.dataset.currency || 'INR';
      const money = new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : this.dataset.locale, {style:'currency', currency, maximumFractionDigits:2, minimumFractionDigits:0});
      if (products.length) {
        content.append(this.element('h3', '', this.dataset.products));
        const list = this.element('ul', 'dxsr__matches');
        list.setAttribute('role', 'list');
        const names = {'bao':'BAO Refresh','dietxp-nrgt-energy-drink-mix-masala-tea':'NRGT Masala','dietxp-nrgt-peach-tea-energy-drink-mix':'NRGT Peach','dietxp-nrgt-lemon-tea-energy-drink-mix':'NRGT Lemon'};
        products.forEach(product => {
          const card = this.link(product.url, 'dxsr-product dxsr-product--compact');
          const media = this.element('div', 'dxsr-product__image');
          const source = product.featured_image?.url || product.image;
          if (source) {
            const url = new URL(source, location.origin);
            if (['http:', 'https:'].includes(url.protocol)) {
              url.searchParams.set('width', '160');
              const image = this.element('img');
              image.src = url.href;
              image.alt = '';
              image.width = 80;
              image.height = 80;
              image.loading = 'lazy';
              media.append(image);
            }
          }
          const copy = this.element('div', 'dxsr-product__body');
          copy.append(this.element('h4', '', names[product.handle] || product.title));
          const varies = Number(product.price_max) > Number(product.price_min);
          copy.append(this.element('strong', 'dxsr-product__price', (varies ? this.dataset.from + ' ' : '') + money.format(Number(product.price))));
          if (Number(product.compare_at_price_min) > Number(product.price)) copy.append(this.element('s', 'dxsr-product__compare', money.format(Number(product.compare_at_price_min))));
          if (!product.available) copy.append(this.element('span', 'dxsr-product__stock', this.dataset.soldOut));
          card.append(media, copy);
          const item = this.element('li');
          item.append(card);
          list.append(item);
        });
        content.append(list);
      }
      [[pages, this.dataset.pages], [articles, this.dataset.articles]].forEach(([items, title]) => {
        if (!items.length) return;
        content.append(this.element('h3', '', title));
        const list = this.element('ul', 'dxsr__article-matches');
        list.setAttribute('role', 'list');
        items.forEach(item => {
          const link = this.link(item.url);
          link.append(this.element('span', '', item.title), this.arrow());
          const row = this.element('li');
          row.append(link);
          list.append(row);
        });
        content.append(list);
      });
      if (Number(content.dataset.count) === 0) {
        const empty = this.element('div', 'dxsr__empty');
        empty.append(this.element('h3', '', this.dataset.noMatches), this.element('p', '', this.dataset.tryAgain));
        content.append(empty);
      }
      const all = new URL(this.dataset.searchUrl, location.origin);
      all.searchParams.set('q', query);
      all.searchParams.set('type', 'product,page,article');
      all.searchParams.set('options[prefix]', 'last');
      const link = this.link(all.href, 'dxsr__all');
      link.append(this.element('span', '', this.dataset.viewResults.replace('[terms]', query)), this.arrow());
      content.append(link);
      return content;
    }
    async search(query, version) {
      this.controller = new AbortController();
      this.status.textContent = this.dataset.loading;
      this.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.endpoint.replace(/\.json$/, '') + '.json', location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,page,article');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      try {
        const response = await fetch(url.toString(), { signal: this.controller.signal });
        if (!response.ok) throw new Error('Search request failed (' + response.status + ')');
        const data = await response.json();
        if (!data.resources?.results) throw new Error('Search response missing');
        const content = this.renderResults(data.resources.results, query);
        if (version !== this.version) return;
        this.results.replaceChildren(content);
        this.results.hidden = false;
        this.status.textContent = this.dataset.ready.replace('[count]', content.dataset.count);
        if (this.discover) this.discover.hidden = Number(content.dataset.count) > 0;
      } catch (error) {
        if (error.name !== 'AbortError' && version === this.version) {
          console.warn('DietXP search unavailable:', error.message);
          this.status.textContent = this.dataset.error;
          if (this.discover) this.discover.hidden = false;
        }
      } finally {
        if (version === this.version) this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('dietxp-search', DietXPSearch);
  let opener;
  let previousOverflow;
  const getDialog = () => document.getElementById('DietXPSearchDialog');
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-dx-search-open], [data-side-drawer="#Drawer-Search"]');
    if (trigger) {
      const dialog = getDialog();
      if (!dialog || typeof dialog.showModal !== 'function') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (dialog.open) return;
      opener = trigger.matches('a,button') ? trigger : trigger.querySelector('a,button') || trigger;
      previousOverflow = document.body.style.overflow;
      dialog.showModal();
      document.body.style.overflow = 'hidden';
      dialog.querySelector('input[type="search"]').focus({ preventScroll: true });
      return;
    }
    const chip = event.target.closest('[data-dx-search-term]');
    if (chip) {
      const search = chip.closest('dialog, .dxsr-results')?.querySelector('dietxp-search');
      if (!search?.input) return;
      event.preventDefault();
      search.input.value = chip.dataset.dxSearchTerm;
      search.update();
      search.input.focus({ preventScroll: true });
    }
  }, true);
  const dialog = getDialog();
  if (!dialog) return;
  dialog.querySelector('[data-dx-search-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    document.body.style.overflow = previousOverflow || '';
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });
})();
